import React, { useState, useEffect } from "react";
import CropAvatar from "./CropAvatar";
import labels from "../data/labels";
import { compressImage } from "../utils/imageUtils";
import Select from 'react-select';

const supportedLanguages = ["de", "cs", "bg"];

const ProfileSetup = ({ onProfileSet }) => {
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState("🐱");
  const [uploadedAvatar, setUploadedAvatar] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [rawImage, setRawImage] = useState(null);
  const [language, setLanguage] = useState("de");
  const t = labels[language] || labels["de"];
  const allProfiles = JSON.parse(localStorage.getItem("blitzlesenProfiles") || "{}");
  const usernames = Object.keys(allProfiles);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let changed = false;
    Object.values(allProfiles).forEach(profile => {
      if (!supportedLanguages.includes(profile.language)) {
        profile.language = "de";
        changed = true;
      }
    });
    if (changed) {
      localStorage.setItem("blitzlesenProfiles", JSON.stringify(allProfiles));
    }
  }, []);

  useEffect(() => {
    if (selected && allProfiles[selected]) {
      const av = allProfiles[selected].avatar;
      if (typeof av === 'string' && av.startsWith('data:image/')) {
        setUploadedAvatar(av);
        setAvatar('');
      } else {
        setUploadedAvatar(null);
        setAvatar(av);
      }
    }
    if (!selected) {
      setUploadedAvatar(null);
      setAvatar('🐱');
    }
  }, [selected]);

  const handleSubmit = (e) => {
    e.preventDefault();

    let finalProfile;
    if (selected) {
      finalProfile = { ...allProfiles[selected] };
      if (!supportedLanguages.includes(finalProfile.language)) {
        finalProfile.language = "de";
      }
      if (uploadedAvatar) {
        finalProfile.avatar = uploadedAvatar;
      } else {
        finalProfile.avatar = avatar;
      }
      allProfiles[selected] = finalProfile;
    } else {
      if (!name.trim()) return;
      const avatarToSave = uploadedAvatar ? uploadedAvatar : avatar;
      finalProfile = {
        username: name.trim(),
        language,
        avatar: avatarToSave,
        scores: {
          flash: [],
          multiple: [],
          timed: []
        }
      };
      allProfiles[finalProfile.username] = finalProfile;
    }

    localStorage.setItem("blitzlesenProfiles", JSON.stringify(allProfiles));
    localStorage.setItem("blitzlesenCurrentUser", finalProfile.username);
    onProfileSet(finalProfile);
  };

  const handleAvatarSelect = (icon) => {
    if (uploadedAvatar) return;
    setAvatar(icon);
  };

  return (
    <div className="profile-setup">
      {showCropper && rawImage && (
        <CropAvatar
          image={rawImage}
          onCropped={(cropped) => {
            setUploadedAvatar(cropped);
            setShowCropper(false);
            setAvatar("");
          }}
          onCancel={() => {
            setShowCropper(false);
            setRawImage(null);
          }}
        />
      )}
      <h2>{t.whoIsPlaying}</h2>
      <form onSubmit={handleSubmit}>
        {usernames.length > 0 && (
          <div className="profile-row">
            <label>{t.selectExistingProfile}</label>
            <div className="profile-field" style={{ minWidth: 0, flex: 1 }}>
              <Select
                options={usernames.map((user) => ({
                  value: user,
                  label: (
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {typeof allProfiles[user].avatar === 'string' && allProfiles[user].avatar.startsWith('data:image/') ? (
                        <img src={allProfiles[user].avatar} alt="avatar" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', marginRight: 8 }} />
                      ) : (
                        <span style={{ fontSize: 28, marginRight: 8 }}>{allProfiles[user].avatar}</span>
                      )}
                      <span style={{ fontSize: 18 }}>{user}</span>
                    </span>
                  ),
                }))}
                value={selected ? usernames.map((user) => ({ value: user, label: user })).find(opt => opt.value === selected) : null}
                onChange={opt => setSelected(opt ? opt.value : null)}
                isClearable
                placeholder={t.selectExistingProfile === "Vorhandenes Profil wählen:" ? "Neues Profil" : t.enterName}
                styles={{
                  control: (base) => ({ ...base, minHeight: 44 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              {selected && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Profil \"${selected}\" wirklich löschen?`)) {
                      delete allProfiles[selected];
                      localStorage.setItem("blitzlesenProfiles", JSON.stringify(allProfiles));
                      setSelected(null);
                      setName("");
                    }
                  }}
                  style={{ marginLeft: "10px", padding: "6px 12px", backgroundColor: "#f88" }}
                >
                  {t.deleteProfile}
                </button>
              )}
            </div>
          </div>
        )}
        {!selected && (
          <>
            <div className="profile-row">
              <label>{t.selectLanguage}</label>
              <div className="profile-field">
                <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                  <option value="de">Deutsch</option>
                  <option value="cs">Čeština</option>
                  <option value="bg">Български</option>
                </select>
              </div>
            </div>
            <div className="profile-row">
              <label>{t.enterName}</label>
              <div className="profile-field">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.enterName}
                  className="name-input"
                />
              </div>
            </div>
            <div className="profile-row">
              <label>{t.chooseAvatar}</label>
              <div className="profile-field avatar-grid">
                {["🐱", "🦊", "🐼", "🐵", "🐸"].map((icon) => (
                  <button
                    type="button"
                    key={icon}
                    onClick={() => handleAvatarSelect(icon)}
                    className={avatar === icon ? "selected" : ""}
                    disabled={!!uploadedAvatar}
                    style={uploadedAvatar ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
            <div className="profile-row">
              <label>{t.orUploadAvatar}</label>
              <div className="profile-field">
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    setError("");
                    const file = e.target.files[0];
                    if (file) {
                      try {
                        const compressed = await compressImage(file, 256, 0.7);
                        const sizeInBytes = Math.ceil((compressed.length * 3) / 4);
                        if (sizeInBytes > 10 * 1024 * 1024) {
                          setError("Image too large after compression (max 10MB). Please choose a smaller image.");
                          return;
                        }
                        setRawImage(compressed);
                        setShowCropper(true);
                      } catch (err) {
                        setError("Failed to process image. Please try another file.");
                      }
                    }
                  }}
                />
              </div>
            </div>
            {error && (
              <div className="error-message">{error}</div>
            )}
          </>
        )}
        {(uploadedAvatar || avatar) && (
          <div style={{ marginTop: "10px", textAlign: "center" }}>
            <strong>{t.preview}</strong><br />
            {uploadedAvatar ? (
              <>
                <img src={uploadedAvatar} alt="Avatar" style={{ height: "80px", borderRadius: "10px", marginTop: "5px" }} />
                <div>
                  <button type="button" className="small-button" style={{ marginTop: 8 }} onClick={() => setUploadedAvatar(null)}>
                    {t.removePhoto || "Remove photo"}
                  </button>
                </div>
              </>
            ) : (
              <span style={{ fontSize: "64px", display: "inline-block", marginTop: "5px" }}>{avatar}</span>
            )}
          </div>
        )}
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <button type="submit" className="green-button">
            {t.letsGo}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileSetup;